import type { ProductImageEntry, ProductImageText } from "./types";

/**
 * The CAIRE mobile app from the 2026-10 surface campaign: the employee app,
 * the handbook, and the client and family views. Stills come from the mobile
 * journeys lane (M1 walks every screen in Swedish and English) and are
 * imported with `yarn product-images:import-mobile`. Screens the app has not
 * translated yet keep the Swedish interface in the English image and say so.
 */
type Draft = {
  id: string;
  module: string;
  opens: string;
  setup: readonly string[];
  sv: ProductImageText;
  en: ProductImageText;
  /** The screen is not translated yet: the English image shows Swedish. */
  swedishInterface?: true;
};

const SWEDISH_UI_EN = "The interface is shown in Swedish.";

function entry(draft: Draft): ProductImageEntry {
  return {
    id: draft.id,
    module: draft.module,
    area: "mobile",
    opens: draft.opens,
    setup: draft.setup,
    scenario: "S3",
    visual: { variant: 0 },
    text: {
      sv: draft.sv,
      en: draft.swedishInterface
        ? { ...draft.en, shows: `${draft.en.shows} ${SWEDISH_UI_EN}` }
        : draft.en,
    },
    use: ["help"],
  };
}

const EMPLOYEE = "Medarbetarappen";
const HANDBOOK = "Handboken";
const CLIENT = "Kundappen";
const FAMILY = "Kundappen för anhöriga";

const SIGN_IN_EMPLOYEE = "Logga in som medarbetare";
const SIGN_IN_CLIENT = "Logga in som kund";
const SIGN_IN_FAMILY = "Logga in som anhörig";

export const CAMPAIGN_MOBILE_CATALOGUE: readonly ProductImageEntry[] = [
  // ── Start ───────────────────────────────────────────────────────────────
  entry({
    id: "mobile.employee.choose-role",
    module: EMPLOYEE,
    opens: "Välj roll",
    setup: ["Logga in med ett konto som är både medarbetare och kund"],
    swedishInterface: true,
    sv: {
      alt: "Frågan Hur vill du använda appen? med valen Som kund och Som medarbetare",
      shows:
        "Den som har både en medarbetarroll och en kundroll väljer hur appen ska användas.",
      proves:
        "Samma inloggning kan öppna medarbetarappen eller kundappen. Användaren väljer roll, och appen visar bara det den rollen har tillgång till.",
    },
    en: {
      alt: "The question How do you want to use the app? with the choices As a client and As an employee",
      shows:
        "Someone with both an employee role and a client role chooses how to use the app.",
      proves:
        "One sign-in can open the employee app or the client app. The user picks a role, and the app shows only what that role can access.",
    },
  }),
  entry({
    id: "mobile.employee.accept-invitation",
    module: EMPLOYEE,
    opens: "Inbjudningslänk",
    setup: ["Öppna inbjudningslänken från kontoret på telefonen"],
    sv: {
      alt: "Appen tar emot en inbjudan från kontoret",
      shows:
        "Medarbetaren öppnar inbjudan från kontoret och kommer in i appen.",
      proves:
        "Kontoret bjuder in medarbetaren från Caire. Inbjudningslänken öppnar appen och kopplar kontot till rätt organisation.",
    },
    en: {
      alt: "The app accepting an invitation from the office",
      shows:
        "The employee opens the office's invitation and gets into the app.",
      proves:
        "The office invites the employee from Caire. The invitation link opens the app and connects the account to the right organization.",
    },
  }),
  entry({
    id: "mobile.employee.offline",
    module: EMPLOYEE,
    opens: "Valfri skärm utan uppkoppling",
    setup: ["Stäng av uppkopplingen", "Gör en ändring i appen"],
    sv: {
      alt: "Banderollen Offline med antalet sparade ändringar",
      shows:
        "Utan uppkoppling visar appen att den är offline och hur många ändringar som väntar.",
      proves:
        "Ändringar som görs utan uppkoppling sparas i telefonen och skickas när uppkopplingen är tillbaka. Banderollen visar hur många som väntar.",
    },
    en: {
      alt: "The Offline banner with the number of saved changes",
      shows:
        "Without a connection the app shows that it is offline and how many changes are waiting.",
      proves:
        "Changes made without a connection are saved on the phone and sent when the connection returns. The banner shows how many are waiting.",
    },
  }),

  // ── Medarbetarappen: flikar ─────────────────────────────────────────────
  entry({
    id: "mobile.employee.home",
    module: EMPLOYEE,
    opens: "Medarbetarappen → Hem",
    setup: [SIGN_IN_EMPLOYEE, "Öppna Hem"],
    sv: {
      alt: "Medarbetarappens startsida med dagens besök",
      shows:
        "Startsidan visar dagens besök, hur många som är klara och vad som är kvar.",
      proves:
        "Medarbetaren ser sin arbetsdag direkt efter inloggning: besöken i det publicerade schemat, vilka som är klara och vilka som återstår.",
    },
    en: {
      alt: "The employee app home screen with today's visits",
      shows:
        "The home screen shows today's visits, how many are done and what is left.",
      proves:
        "Employees see their working day right after signing in: the visits in the published schedule, which are done and which remain.",
    },
  }),
  entry({
    id: "mobile.employee.schedule",
    module: EMPLOYEE,
    opens: "Medarbetarappen → Schema",
    setup: [SIGN_IN_EMPLOYEE, "Öppna Schema"],
    sv: {
      alt: "Schemat i medarbetarappen med dagens besök i tidsordning",
      shows:
        "Schemat visar dagens besök i tidsordning, med lägena Dag och Vecka.",
      proves:
        "Medarbetaren ser sina besök i det publicerade schemat dag för dag och kan växla till veckovy.",
    },
    en: {
      alt: "The schedule in the employee app with today's visits in order",
      shows:
        "The schedule lists today's visits in order, with Day and Week views.",
      proves:
        "Employees see their visits in the published schedule day by day and can switch to a week view.",
    },
  }),
  entry({
    id: "mobile.employee.map",
    module: EMPLOYEE,
    opens: "Medarbetarappen → Karta",
    setup: [SIGN_IN_EMPLOYEE, "Öppna Karta"],
    sv: {
      alt: "Kartan i medarbetarappen med dagens besök",
      shows: "Kartan visar dagens besök, och filtren Alla, Besök och Kollegor.",
      proves:
        "Medarbetaren ser dagens besök på kartan och kan öppna rutten mellan dem.",
    },
    en: {
      alt: "The map in the employee app with today's visits",
      shows:
        "The map shows today's visits, with the filters All, Visits and Colleagues.",
      proves:
        "Employees see the day's visits on the map and can open the route between them.",
    },
  }),
  entry({
    id: "mobile.employee.profile",
    module: EMPLOYEE,
    opens: "Medarbetarappen → Profil",
    setup: [SIGN_IN_EMPLOYEE, "Öppna Profil"],
    sv: {
      alt: "Medarbetarens profil med roll, kontaktuppgifter och anställningsgrad",
      shows:
        "Profilen visar roll, e-post, telefon, anställningsgrad och timmar per vecka.",
      proves:
        "Medarbetaren ser de uppgifter kontoret har registrerat om anställningen.",
    },
    en: {
      alt: "The employee's profile with role, contact details and employment rate",
      shows:
        "The profile shows role, email, phone, employment rate and hours per week.",
      proves: "Employees see the employment details the office has registered.",
    },
  }),
  entry({
    id: "mobile.employee.profile-photo",
    module: EMPLOYEE,
    opens: "Medarbetarappen → Profil → Profilfoto",
    setup: [SIGN_IN_EMPLOYEE, "Öppna Profil", "Scrolla till Profilfoto"],
    swedishInterface: true,
    sv: {
      alt: "Kortet Profilfoto med knapparna Ta foto och Välj",
      shows: "Medarbetaren tar eller väljer ett eget profilfoto.",
      proves:
        "Profilfotot läggs till direkt från telefonen med kameran eller från bildbiblioteket.",
    },
    en: {
      alt: "The profile photo card with the buttons Take photo and Choose",
      shows: "Employees take or choose their own profile photo.",
      proves:
        "The profile photo is added straight from the phone, with the camera or from the photo library.",
    },
  }),
  entry({
    id: "mobile.employee.profile-review",
    module: EMPLOYEE,
    opens: "Granska din profil",
    setup: ["Logga in för första gången som ny medarbetare"],
    swedishInterface: true,
    sv: {
      alt: "Sidan Välkommen till CAIRE där medarbetaren granskar sin profil",
      shows:
        "Vid första inloggningen granskar medarbetaren sin profil och hittar till Handbok, Introduktion och Hjälp.",
      proves:
        "En ny medarbetare möts av sin profil innan arbetet börjar och kan rätta uppgifter och hitta introduktionen.",
    },
    en: {
      alt: "The Welcome to CAIRE page where the employee reviews their profile",
      shows:
        "At first sign-in the employee reviews their profile and finds the Handbook, Onboarding and Help.",
      proves:
        "A new employee is met by their profile before work starts and can correct details and find the onboarding.",
    },
  }),
  entry({
    id: "mobile.employee.more",
    module: EMPLOYEE,
    opens: "Medarbetarappen → Mer",
    setup: [SIGN_IN_EMPLOYEE, "Öppna Mer"],
    swedishInterface: true,
    sv: {
      alt: "Menyn Mer med sjukanmälan, passbyte, tillgänglighet och fler val",
      shows:
        "Mer samlar Sjukanmälan, Byt pass, Tillgänglighet, Kollegor, Kontakter, Aviseringar, Språk och Hjälp.",
      proves:
        "Medarbetaren sköter sjukanmälan, passbyten och tillgänglighet själv i appen, och planeraren får förfrågan i Caire.",
    },
    en: {
      alt: "The More menu with sick leave, shift swap, availability and other options",
      shows:
        "More gathers sick leave, shift swap, availability, colleagues, contacts, notifications, language and help.",
      proves:
        "Employees handle sick leave, shift swaps and availability themselves in the app, and the planner receives the request in Caire.",
    },
  }),
  entry({
    id: "mobile.employee.notifications",
    module: EMPLOYEE,
    opens: "Medarbetarappen → Aviseringar",
    setup: [SIGN_IN_EMPLOYEE, "Öppna Aviseringar"],
    swedishInterface: true,
    sv: {
      alt: "Aviseringar i medarbetarappen, bland annat Ditt schema har uppdaterats",
      shows:
        "Aviseringarna samlar händelser som rör medarbetaren, till exempel att schemat har uppdaterats.",
      proves:
        "När planeraren ändrar i det publicerade schemat får medarbetaren en avisering och kan se den i appen.",
    },
    en: {
      alt: "Notifications in the employee app, including Your schedule has been updated",
      shows:
        "Notifications gather events that concern the employee, such as a schedule update.",
      proves:
        "When the planner changes the published schedule, the employee gets a notification and can see it in the app.",
    },
  }),
  entry({
    id: "mobile.employee.visit-detail",
    module: EMPLOYEE,
    opens: "Medarbetarappen → Schema → besök",
    setup: [SIGN_IN_EMPLOYEE, "Öppna Schema", "Välj ett besök"],
    sv: {
      alt: "Ett besök i medarbetarappen med knappen Checka in",
      shows:
        "Besöket visar tid, adress, kontakt och insatser, och knappen Checka in.",
      proves:
        "Medarbetaren har allt som behövs inför besöket på ett ställe och checkar in direkt från besöket.",
    },
    en: {
      alt: "A visit in the employee app with the Check in button",
      shows:
        "The visit shows time, address, contact and tasks, and the Check in button.",
      proves:
        "Employees have everything they need before the visit in one place and check in straight from the visit.",
    },
  }),
  entry({
    id: "mobile.employee.voice-note",
    module: EMPLOYEE,
    opens: "Besök → Anteckningar → Tala in anteckning",
    setup: [
      SIGN_IN_EMPLOYEE,
      "Checka in på ett besök",
      "Välj Tala in anteckning",
    ],
    sv: {
      alt: "Panelen Tala in anteckning under ett besök",
      shows:
        "Under besöket talar medarbetaren in en anteckning i stället för att skriva.",
      proves:
        "Det inspelade blir text som medarbetaren läser igenom innan anteckningen sparas på besöket.",
    },
    en: {
      alt: "The Record a note panel during a visit",
      shows: "During the visit the employee records a note instead of typing.",
      proves:
        "The recording becomes text that the employee reads through before the note is saved on the visit.",
    },
  }),

  // ── Medarbetarappen: Mer ────────────────────────────────────────────────
  entry({
    id: "mobile.employee.sick-leave",
    module: EMPLOYEE,
    opens: "Medarbetarappen → Mer → Sjukanmälan",
    setup: [SIGN_IN_EMPLOYEE, "Öppna Mer", "Välj Sjukanmälan"],
    swedishInterface: true,
    sv: {
      alt: "Sjukanmälan med första och sista sjukdag",
      shows:
        "Medarbetaren anger första och sista sjukdag och skickar sjukanmälan.",
      proves:
        "Sjukanmälan går direkt till planeraren i Caire, och medarbetaren ser beslutet i sin historik.",
    },
    en: {
      alt: "Sick leave with first and last day of sickness",
      shows:
        "The employee enters the first and last day of sickness and sends the report.",
      proves:
        "The sick leave report goes straight to the planner in Caire, and the employee sees the decision in their history.",
    },
  }),
  entry({
    id: "mobile.employee.shift-swap",
    module: EMPLOYEE,
    opens: "Medarbetarappen → Mer → Byt pass",
    setup: [SIGN_IN_EMPLOYEE, "Öppna Mer", "Välj Byt pass"],
    swedishInterface: true,
    sv: {
      alt: "Byt pass med knappen Ny bytesförfrågan",
      shows:
        "Medarbetaren ber om att byta ett pass och följer sina bytesförfrågningar.",
      proves:
        "Bytesförfrågan går till planeraren, som godkänner bytet innan schemat ändras.",
    },
    en: {
      alt: "Shift swap with the New swap request button",
      shows:
        "The employee asks to swap a shift and follows their swap requests.",
      proves:
        "The swap request goes to the planner, who approves the swap before the schedule changes.",
    },
  }),
  entry({
    id: "mobile.employee.offer-shift",
    module: EMPLOYEE,
    opens: "Medarbetarappen → Mer → Erbjud extra pass",
    setup: [SIGN_IN_EMPLOYEE, "Öppna Erbjud extra pass"],
    sv: {
      alt: "Erbjud extra pass med val av datum och pass",
      shows: "Medarbetaren erbjuder ett extra pass en dag då hen kan arbeta.",
      proves:
        "Planeraren granskar erbjudandet innan passet läggs till i schemat.",
    },
    en: {
      alt: "Offer an extra shift with a choice of date and shift",
      shows: "The employee offers an extra shift on a day they can work.",
      proves:
        "The planner reviews the offer before the shift is added to the schedule.",
    },
  }),
  entry({
    id: "mobile.employee.open-shifts",
    module: EMPLOYEE,
    opens: "Medarbetarappen → Mer → Öppna pass",
    setup: [SIGN_IN_EMPLOYEE, "Öppna Öppna pass"],
    sv: {
      alt: "Öppna pass med inbjudningar från planeraren",
      shows:
        "Här samlas de öppna pass som planeraren har bjudit in medarbetaren till.",
      proves:
        "Säger medarbetaren ja blir det ett erbjudande som planeraren godkänner.",
    },
    en: {
      alt: "Open shifts with invitations from the planner",
      shows:
        "This lists the open shifts the planner has invited the employee to.",
      proves:
        "If the employee says yes, it becomes an offer that the planner approves.",
    },
  }),
  entry({
    id: "mobile.employee.availability",
    module: EMPLOYEE,
    opens: "Medarbetarappen → Mer → Tillgänglighet",
    setup: [SIGN_IN_EMPLOYEE, "Öppna Mer", "Välj Tillgänglighet"],
    swedishInterface: true,
    sv: {
      alt: "Tillgänglighet med föredragna arbetsdagar och blockerade datum",
      shows:
        "Medarbetaren väljer föredragna arbetsdagar och lägger till datum då hen inte kan arbeta.",
      proves:
        "Det som sparas med Spara tillgänglighet följer med till planeringen som medarbetarens tillgänglighet.",
    },
    en: {
      alt: "Availability with preferred working days and blocked dates",
      shows:
        "The employee chooses preferred working days and adds dates they cannot work.",
      proves:
        "What is saved with Save availability carries over to planning as the employee's availability.",
    },
  }),
  entry({
    id: "mobile.employee.help-requests",
    module: EMPLOYEE,
    opens: "Medarbetarappen → Mer → Kollegor",
    setup: [SIGN_IN_EMPLOYEE, "Öppna Mer", "Välj Kollegor"],
    swedishInterface: true,
    sv: {
      alt: "Kollegor med öppna hjälpfrågor och kollegor på kartan",
      shows:
        "Medarbetaren ser öppna hjälpfrågor och vilka kollegor som finns i närheten.",
      proves:
        "En kollega kan ta ett besök som behöver hjälp med Ta besöket, eller ringa och skicka SMS direkt från listan.",
    },
    en: {
      alt: "Colleagues with open help requests and colleagues on the map",
      shows:
        "The employee sees open help requests and which colleagues are nearby.",
      proves:
        "A colleague can take a visit that needs help, or call and text directly from the list.",
    },
  }),
  entry({
    id: "mobile.employee.dictated-sms",
    module: EMPLOYEE,
    opens: "Medarbetarappen → Kollegor → Diktera SMS",
    setup: [
      SIGN_IN_EMPLOYEE,
      "Öppna Kollegor",
      "Välj Diktera SMS på en kollega",
    ],
    sv: {
      alt: "Panelen Diktera SMS till en kollega",
      shows: "Medarbetaren talar in ett SMS till en kollega.",
      proves:
        "Det inspelade blir text som öppnas i telefonens meddelandeapp, där medarbetaren själv skickar SMS:et.",
    },
    en: {
      alt: "The Dictate SMS panel for a colleague",
      shows: "The employee dictates a text message to a colleague.",
      proves:
        "The recording becomes text that opens in the phone's messaging app, where the employee sends it themselves.",
    },
  }),
  entry({
    id: "mobile.employee.contacts",
    module: EMPLOYEE,
    opens: "Medarbetarappen → Mer → Kontakter",
    setup: [SIGN_IN_EMPLOYEE, "Öppna Mer", "Välj Kontakter"],
    swedishInterface: true,
    sv: {
      alt: "Kontakter med knappen Ring kontoret",
      shows: "Kontakter visar hur medarbetaren når kontoret.",
      proves: "Medarbetaren ringer kontoret direkt från appen.",
    },
    en: {
      alt: "Contacts with the Call the office button",
      shows: "Contacts shows how the employee reaches the office.",
      proves: "Employees call the office directly from the app.",
    },
  }),
  entry({
    id: "mobile.employee.notification-settings",
    module: EMPLOYEE,
    opens: "Medarbetarappen → Mer → Aviseringsinställningar",
    setup: [SIGN_IN_EMPLOYEE, "Öppna Mer", "Välj Aviseringsinställningar"],
    swedishInterface: true,
    sv: {
      alt: "Aviseringsinställningar med schemaändringar, påminnelser och Proaktiva AI-tips",
      shows:
        "Medarbetaren väljer vilka aviseringar som ska komma, till exempel när schemat ändras och påminnelser inför besök.",
      proves:
        "Varje medarbetare styr sina egna aviseringar, också om dagassistenten ska visa proaktiva AI-tips.",
    },
    en: {
      alt: "Notification settings with schedule changes, reminders and proactive AI tips",
      shows:
        "The employee chooses which notifications to get, such as schedule changes and reminders before visits.",
      proves:
        "Each employee controls their own notifications, including whether the day assistant shows proactive AI tips.",
    },
  }),
  entry({
    id: "mobile.employee.language",
    module: EMPLOYEE,
    opens: "Medarbetarappen → Mer → Språk",
    setup: [SIGN_IN_EMPLOYEE, "Öppna Mer", "Välj Språk"],
    swedishInterface: true,
    sv: {
      alt: "Språkvalet med Svenska och English",
      shows:
        "Medarbetaren väljer om appen ska visas på svenska eller engelska.",
      proves:
        "Appen finns på svenska och engelska, och valet görs per medarbetare.",
    },
    en: {
      alt: "The language choice with Svenska and English",
      shows:
        "The employee chooses whether the app is shown in Swedish or English.",
      proves:
        "The app is available in Swedish and English, and each employee makes the choice.",
    },
  }),
  entry({
    id: "mobile.employee.help",
    module: EMPLOYEE,
    opens: "Medarbetarappen → Mer → Hjälp",
    setup: [SIGN_IN_EMPLOYEE, "Öppna Mer", "Välj Hjälp"],
    swedishInterface: true,
    sv: {
      alt: "Hjälp för medarbetare i appen",
      shows: "Hjälpen förklarar hur medarbetaren använder appen.",
      proves:
        "Hjälpen finns direkt i appen och beskriver hur medarbetaren ser dagens besök, dokumenterar och håller kontakt med kontoret.",
    },
    en: {
      alt: "Help for employees in the app",
      shows: "The help explains how employees use the app.",
      proves:
        "The help is built into the app and describes how employees see the day's visits, document and stay in touch with the office.",
    },
  }),

  // ── Handboken ───────────────────────────────────────────────────────────
  entry({
    id: "mobile.handbook.index",
    swedishInterface: true,
    module: HANDBOOK,
    opens: "Medarbetarappen → Mer → Handbok",
    setup: [SIGN_IN_EMPLOYEE, "Öppna Mer", "Välj Handbok"],
    sv: {
      alt: "Handboken i medarbetarappen med dess avsnitt",
      shows: "Handboken samlar verksamhetens rutiner i avsnitt.",
      proves:
        "Medarbetaren läser verksamhetens handbok i appen, i den version kontoret har publicerat.",
    },
    en: {
      alt: "The handbook in the employee app with its sections (Swedish interface)",
      shows: "The handbook gathers the organization's routines in sections.",
      proves:
        "Employees read the organization's handbook in the app, in the version the office has published.",
    },
  }),
  entry({
    id: "mobile.handbook.page",
    swedishInterface: true,
    module: HANDBOOK,
    opens: "Handbok → sida",
    setup: [SIGN_IN_EMPLOYEE, "Öppna Handbok", "Välj en sida"],
    sv: {
      alt: "En sida i handboken",
      shows: "En handbokssida med en rutin.",
      proves: "Varje rutin går att läsa i sin helhet direkt i appen.",
    },
    en: {
      alt: "A page in the handbook (Swedish interface)",
      shows: "A handbook page with a routine.",
      proves: "Each routine can be read in full directly in the app.",
    },
  }),
  entry({
    id: "mobile.handbook.onboarding",
    swedishInterface: true,
    module: HANDBOOK,
    opens: "Medarbetarappen → Mer → Introduktion",
    setup: [SIGN_IN_EMPLOYEE, "Öppna Mer", "Välj Introduktion"],
    sv: {
      alt: "Introduktionen med stegen en ny medarbetare går igenom",
      shows:
        "Introduktionen visar stegen en ny medarbetare går igenom, med knappen Slutför introduktionen.",
      proves:
        "Den nya medarbetaren går igenom introduktionen i appen och markerar den som klar.",
    },
    en: {
      alt: "The onboarding with the steps a new employee goes through (Swedish interface)",
      shows:
        "The onboarding shows the steps a new employee goes through, with a button to complete it.",
      proves:
        "New employees go through the onboarding in the app and mark it as complete.",
    },
  }),
  entry({
    id: "mobile.handbook.onboarding-home-card",
    module: HANDBOOK,
    opens: "Medarbetarappen → Hem → Introduktion",
    setup: [SIGN_IN_EMPLOYEE, "Öppna Hem som ny medarbetare"],
    sv: {
      alt: "Kortet Introduktion på startsidan med hur många steg som är klara",
      shows:
        "Startsidan visar hur långt den nya medarbetaren har kommit i introduktionen.",
      proves:
        "Kortet visar antalet klara steg och leder vidare till introduktionen tills den är slutförd.",
    },
    en: {
      alt: "The onboarding card on the home screen with how many steps are done",
      shows:
        "The home screen shows how far the new employee has come in the onboarding.",
      proves:
        "The card shows the number of completed steps and leads on to the onboarding until it is finished.",
    },
  }),

  // ── Kundappen ───────────────────────────────────────────────────────────
  entry({
    id: "mobile.client.home",
    module: CLIENT,
    opens: "Kundappen → Hem",
    setup: [SIGN_IN_CLIENT],
    sv: {
      alt: "Kundappens startsida med nästa besök",
      shows: "Kunden ser när nästa besök kommer.",
      proves:
        "Kundappen visar kundens besök ur det publicerade schemat, med planerad ankomst.",
    },
    en: {
      alt: "The client app home screen with the next visit",
      shows: "The client sees when the next visit is coming.",
      proves:
        "The client app shows the client's visits from the published schedule, with the planned arrival.",
    },
  }),
  entry({
    id: "mobile.client.visits",
    module: CLIENT,
    opens: "Kundappen → Besök",
    setup: [SIGN_IN_CLIENT, "Öppna Besök"],
    sv: {
      alt: "Kundens besök i kundappen",
      shows: "Besök listar kundens kommande besök.",
      proves:
        "Kunden ser sina planerade besök och när personalen väntas komma.",
    },
    en: {
      alt: "The client's visits in the client app",
      shows: "Visits lists the client's upcoming visits.",
      proves:
        "Clients see their planned visits and when staff are expected to arrive.",
    },
  }),
  entry({
    id: "mobile.client.more",
    module: CLIENT,
    opens: "Kundappen → Mer",
    setup: [SIGN_IN_CLIENT, "Öppna Mer"],
    sv: {
      alt: "Menyn Mer i kundappen med vårdplan, kontaktpersoner och anhöriga",
      shows:
        "Mer samlar Min vårdplan, Dina kontaktpersoner, Anhöriga och hur kunden når kontoret.",
      proves:
        "Kunden hittar sin vårdplan, sina kontaktpersoner och sina anhörigas åtkomst på ett ställe.",
    },
    en: {
      alt: "The More menu in the client app with care plan, contact persons and family",
      shows:
        "More gathers the care plan, contact persons, family and how the client reaches the office.",
      proves:
        "Clients find their care plan, contact persons and family access in one place.",
    },
  }),
  entry({
    id: "mobile.client.care-plan",
    module: CLIENT,
    opens: "Kundappen → Mer → Min vårdplan",
    setup: [SIGN_IN_CLIENT, "Öppna Mer", "Välj Min vårdplan"],
    swedishInterface: true,
    sv: {
      alt: "Min vårdplan i kundappen",
      shows: "Kunden läser sin vårdplan i appen.",
      proves: "Kunden ser varje insats i vårdplanen och hur ofta den ges.",
    },
    en: {
      alt: "My care plan in the client app",
      shows: "The client reads their care plan in the app.",
      proves:
        "Clients see each care task in their care plan and how often it is given.",
    },
  }),
  entry({
    id: "mobile.client.contact-persons",
    module: CLIENT,
    opens: "Kundappen → Mer → Dina kontaktpersoner",
    setup: [SIGN_IN_CLIENT, "Öppna Mer", "Välj Dina kontaktpersoner"],
    swedishInterface: true,
    sv: {
      alt: "Kontaktpersoner med omsorgskontakt och personal kunden oftast får",
      shows:
        "Kunden ser sin omsorgskontakt och den personal som oftast kommer.",
      proves:
        "Kunden vet vem som ansvarar för omsorgen och vilka som brukar komma, och kan skicka önskemål till kontoret.",
    },
    en: {
      alt: "Contact persons with the care contact and the staff the client most often gets",
      shows:
        "The client sees their care contact and the staff who come most often.",
      proves:
        "Clients know who is responsible for their care and who usually comes, and can send requests to the office.",
    },
  }),
  entry({
    id: "mobile.client.family-access",
    module: CLIENT,
    opens: "Kundappen → Mer → Anhöriga",
    setup: [SIGN_IN_CLIENT, "Öppna Mer", "Välj Anhöriga"],
    swedishInterface: true,
    sv: {
      alt: "Anhöriga med åtkomst och inbjudan av anhörig med e-post",
      shows:
        "Kunden ser vilka anhöriga som har åtkomst och bjuder in fler med e-post.",
      proves:
        "Kunden bestämmer själv vilka anhöriga som får följa besöken, och kontoret hanterar inte anhörigkontona.",
    },
    en: {
      alt: "Family members with access and an email invitation for a family member",
      shows:
        "The client sees which family members have access and invites more by email.",
      proves:
        "Clients decide for themselves which family members can follow their visits; the office does not manage family accounts.",
    },
  }),
  entry({
    id: "mobile.client.visit-detail",
    module: CLIENT,
    opens: "Kundappen → Besök → besök",
    setup: [SIGN_IN_CLIENT, "Öppna Besök", "Välj ett besök"],
    swedishInterface: true,
    sv: {
      alt: "Ett besök i kundappen med Vem kommer, När och Status",
      shows: "Besöket visar vem som kommer, när och besökets status.",
      proves:
        "Kunden ser vilken personal som är planerad, den planerade ankomsten och hur länge besöket tar.",
    },
    en: {
      alt: "A visit in the client app with who is coming, when and the status",
      shows: "The visit shows who is coming, when, and the visit's status.",
      proves:
        "Clients see which staff member is scheduled, the planned arrival and how long the visit takes.",
    },
  }),
  entry({
    id: "mobile.client.live-location",
    module: CLIENT,
    opens: "Kundappen → besök som pågår",
    setup: [SIGN_IN_CLIENT, "Öppna ett besök som pågår"],
    swedishInterface: true,
    sv: {
      alt: "Kortet som visar var personalen är under ett pågående besök",
      shows: "När besöket pågår kan kunden se var personalen är.",
      proves:
        "Positionen visas bara när kunden har samtyckt och besöket pågår.",
    },
    en: {
      alt: "The card showing where the staff member is during an ongoing visit",
      shows:
        "While the visit is underway the client can see where the staff member is.",
      proves:
        "The location is shown only when the client has consented and the visit is underway.",
    },
  }),
  entry({
    id: "mobile.client.visit-rating",
    module: CLIENT,
    opens: "Kundappen → genomfört besök → Hur var besöket?",
    setup: [SIGN_IN_CLIENT, "Öppna ett genomfört besök"],
    swedishInterface: true,
    sv: {
      alt: "Frågan Hur var besöket? med stjärnor och en frivillig kommentar",
      shows: "Efter besöket ger kunden ett betyg från 1 till 5 stjärnor.",
      proves:
        "Kunden betygsätter genomförda besök och kan lägga till en kommentar. Betyget går att ändra.",
    },
    en: {
      alt: "The question How was the visit? with stars and an optional comment",
      shows: "After the visit the client gives a rating from 1 to 5 stars.",
      proves:
        "Clients rate completed visits and can add a comment. The rating can be changed.",
    },
  }),
  entry({
    id: "mobile.client.caregiver-explanation",
    module: CLIENT,
    opens: "Kundappen → besök → Vem kommer",
    setup: [SIGN_IN_CLIENT, "Öppna Besök", "Välj ett besök"],
    swedishInterface: true,
    sv: {
      alt: "Kortet Därför kommer under Vem kommer, med skälen till valet av personal",
      shows:
        "Under Vem kommer förklarar appen varför just den personen kommer.",
      proves:
        "Kunden får skälen till att personen är planerad, till exempel att det är någon kunden har träffat förut.",
    },
    en: {
      alt: "The card explaining why this staff member is coming, with the reasons",
      shows: "Under who is coming the app explains why this person is coming.",
      proves:
        "Clients get the reasons this person is scheduled, for example that it is someone they have met before.",
    },
  }),
  entry({
    id: "mobile.client.visit-communication-policy",
    module: CLIENT,
    opens: "Kundappen → besök → Kontakt om besöket",
    setup: [
      SIGN_IN_CLIENT,
      "Öppna ett besök",
      "Scrolla till Kontakt om besöket",
    ],
    swedishInterface: true,
    sv: {
      alt: "Kortet Kontakt om besöket med knappen Ring kontoret",
      shows: "Kortet förklarar hur kunden når kontoret om besöket.",
      proves:
        "Kontoret kan skicka säkra uppdateringar om besöket. Behöver kunden ändra något ringer hen kontoret direkt från kortet.",
    },
    en: {
      alt: "The Contact about the visit card with the Call the office button",
      shows:
        "The card explains how the client reaches the office about the visit.",
      proves:
        "The office can send secure updates about the visit. If the client needs to change something, they call the office straight from the card.",
    },
  }),

  // ── Anhöriga ────────────────────────────────────────────────────────────
  entry({
    id: "mobile.family.visits",
    module: FAMILY,
    opens: "Kundappen för anhöriga → Besök",
    setup: [SIGN_IN_FAMILY],
    sv: {
      alt: "Besöken för den kund den anhöriga följer",
      shows: "Den anhöriga ser besöken för den kund hen följer.",
      proves:
        "Anhöriga som kunden har bjudit in ser bara den kundens besök och kan inte ändra något.",
    },
    en: {
      alt: "The visits of the client the family member follows",
      shows: "The family member sees the visits of the client they follow.",
      proves:
        "Family members the client has invited see only that client's visits and cannot change anything.",
    },
  }),
  entry({
    id: "mobile.family.more",
    module: FAMILY,
    opens: "Kundappen för anhöriga → Mer",
    setup: [SIGN_IN_FAMILY, "Öppna Mer"],
    sv: {
      alt: "Menyn Mer för anhöriga med Du följer och Kontakta kontoret",
      shows:
        "Mer visar vilken kund den anhöriga följer och hur hen når kontoret.",
      proves:
        "Den anhöriga ser vem hen följer, kan skicka önskemål och ringa kontoret.",
    },
    en: {
      alt: "The More menu for family members with who they follow and contacting the office",
      shows:
        "More shows which client the family member follows and how to reach the office.",
      proves:
        "Family members see who they follow, can send requests and call the office.",
    },
  }),
  entry({
    id: "mobile.family.visit-detail",
    module: FAMILY,
    opens: "Kundappen för anhöriga → Besök → besök",
    setup: [SIGN_IN_FAMILY, "Välj ett besök"],
    swedishInterface: true,
    sv: {
      alt: "Ett besök i anhörigvyn med Vem kommer och Status",
      shows: "Den anhöriga ser vem som kommer och besökets status.",
      proves:
        "Anhöriga följer besöket med samma uppgifter som kunden, utan möjlighet att ändra.",
    },
    en: {
      alt: "A visit in the family view with who is coming and the status",
      shows: "The family member sees who is coming and the visit's status.",
      proves:
        "Family members follow the visit with the same details as the client, without being able to change it.",
    },
  }),
];
